import React, { ChangeEvent, Fragment, useEffect, useMemo, useState } from 'react';
import { ChevronRight, LucidePlus, LucideX, Settings, LucideSquarePen } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip-shadcn';
import { cn } from '@/lib/utils';

import type {
  ProjectOverviewColumnKey,
  ProjectOverviewColumnVisibility,
} from './column_visibility';
import { PROJECT_OVERVIEW_COLUMNS, resolveColumnVisibility } from './column_visibility';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { addNewRole, editRole } from '../../api/roles';
import {
  buildRoleAssignmentManageEntries,
  countRoleAssignmentManageEntriesForRole,
} from '../../helper/roleAssignments';
import { showErrorToast } from '../../pages/team_settings';
import Select from '../elements/Select/Select';

const assigneeColumnsReleaseDate = new Date('2026-03-02');
const thirtyDaysAfterAssigneeColumnsRelease = new Date(assigneeColumnsReleaseDate);
thirtyDaysAfterAssigneeColumnsRelease.setDate(thirtyDaysAfterAssigneeColumnsRelease.getDate() + 30);
const showAssigneeColumnsNewBadge = thirtyDaysAfterAssigneeColumnsRelease > new Date();

interface ColumnVisibilityMenuProps {
  value: ProjectOverviewColumnVisibility;
  onChange: (next: ProjectOverviewColumnVisibility) => void;
  showCustomFieldsSection?: boolean;
  canCustomizeCustomColumns?: boolean;
  initialCustomColumns?: CustomColumnPreference[];
  onSaveCustomColumns?: (customColumns: CustomColumnPreference[]) => Promise<void> | void;
  hiddenKeys?: ProjectOverviewColumnKey[];
  disabledKeys?: ProjectOverviewColumnKey[];
  preserveOnHideAll?: ProjectOverviewColumnKey[];
  className?: string;
  roleAssignmentColumns?: Array<{ id: number; name: string }>;
  roleColumnVisibility?: Record<number, boolean>;
  onRoleColumnVisibilityChange?: (next: Record<number, boolean>) => void;
  isAccountAdmin?: boolean;
  teamSettingsRolesPath?: string;
  account?: any;
  handleRefetchRoles?(): void;
  accountUser?: any;
  teamMembers?: any[];
  assignees?: any[];
  handleUpdateTeamMember?(val: any, member: any, role: any): void;
  handleUpdateAssignee?(val: any, assignee: any, role: any): void;
  unsetRoleColumns?: Array<{ id: number; name: string }>;
  handleOpenInvitePanel?(item: string, role?: any): void;
  canAccessTeamSettings?: boolean;
}

type FormErrors = {
  name: {
    active: boolean;
    message: string;
  };
};

const initialFormErrors = {
  name: {
    active: false,
    message: '',
  },
};

type CustomFieldInputType = 'text' | 'dropdown';

type CustomColumnPreference = {
  id: string;
  name: string;
  inputType: CustomFieldInputType;
  options: string[];
};

type CustomFieldConfig = {
  id: string;
  name: string;
  inputType: CustomFieldInputType;
  options: string[];
  pendingOption: string;
  editingOptionIndex: number | null;
  editingOptionValue: string;
};

const MAX_DROPDOWN_OPTIONS = 25;
const CUSTOM_FIELD_INPUT_TYPE_OPTIONS = [
  { label: 'Plain text', value: 'text' as const },
  { label: 'Dropdown', value: 'dropdown' as const },
];

const normalizeDropdownOptions = (options: string[], max = MAX_DROPDOWN_OPTIONS): string[] => {
  if (!Array.isArray(options)) {
    return [];
  }

  const next: string[] = [];
  const seen = new Set<string>();

  options.forEach((option) => {
    const normalized = String(option || '').trim();
    if (!normalized) {
      return;
    }

    const key = normalized.toLowerCase();
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    if (next.length < max) {
      next.push(normalized);
    }
  });

  return next;
};

const getPendingOptionValidationError = (
  field: CustomFieldConfig
): 'duplicate' | 'limit' | null => {
  const trimmedOption = field.pendingOption.trim();
  if (!trimmedOption) {
    return null;
  }

  const normalizedOptions = normalizeDropdownOptions(field.options, MAX_DROPDOWN_OPTIONS);
  if (normalizedOptions.length >= MAX_DROPDOWN_OPTIONS) {
    return 'limit';
  }

  const normalizedKey = trimmedOption.toLowerCase();
  if (normalizedOptions.some((option) => option.toLowerCase() === normalizedKey)) {
    return 'duplicate';
  }

  return null;
};

const INITIAL_CUSTOM_FIELDS: CustomFieldConfig[] = [
  {
    id: 'cc1',
    name: 'Custom Field 1',
    inputType: 'text',
    options: [],
    pendingOption: '',
    editingOptionIndex: null,
    editingOptionValue: '',
  },
  {
    id: 'cc2',
    name: 'Custom Field 2',
    inputType: 'text',
    options: [],
    pendingOption: '',
    editingOptionIndex: null,
    editingOptionValue: '',
  },
  {
    id: 'cc3',
    name: 'Custom Field 3',
    inputType: 'text',
    options: [],
    pendingOption: '',
    editingOptionIndex: null,
    editingOptionValue: '',
  },
];

const cloneCustomFields = (fields: CustomFieldConfig[]): CustomFieldConfig[] =>
  fields.map((field) => ({
    ...field,
    options: Array.isArray(field.options) ? [...field.options] : [],
    pendingOption: field.pendingOption || '',
    editingOptionIndex:
      typeof field.editingOptionIndex === 'number' ? field.editingOptionIndex : null,
    editingOptionValue: field.editingOptionValue || '',
  }));

const normalizeCustomFieldForSave = (field: CustomFieldConfig): CustomFieldConfig => ({
  ...field,
  name: field.name.trim() || 'Custom Field',
  options: normalizeDropdownOptions(field.options, MAX_DROPDOWN_OPTIONS),
  pendingOption: '',
  editingOptionIndex: null,
  editingOptionValue: '',
});

const normalizeCustomColumnsForState = (
  incomingColumns: CustomColumnPreference[] | undefined
): CustomFieldConfig[] => {
  if (!Array.isArray(incomingColumns) || incomingColumns.length === 0) {
    return cloneCustomFields(INITIAL_CUSTOM_FIELDS);
  }

  const sanitizedIncoming = incomingColumns.map((column, index) => ({
    id: String(column?.id || `cc${index + 1}`),
    name: String(column?.name || `Custom Field ${index + 1}`),
    inputType: column?.inputType === 'dropdown' ? 'dropdown' : 'text',
    options: normalizeDropdownOptions(
      Array.isArray(column?.options) ? column.options.map((option) => String(option)) : [],
      MAX_DROPDOWN_OPTIONS
    ),
    pendingOption: '',
    editingOptionIndex: null,
    editingOptionValue: '',
  }));

  const fallbackColumns = cloneCustomFields(INITIAL_CUSTOM_FIELDS);
  const merged = fallbackColumns.map((fallback, index) => {
    const incoming = sanitizedIncoming[index];
    if (!incoming) {
      return fallback;
    }
    return {
      ...fallback,
      ...incoming,
      id: incoming.id || fallback.id,
      name: incoming.name || fallback.name,
      inputType: incoming.inputType || fallback.inputType,
      options: incoming.options || [],
      pendingOption: '',
      editingOptionIndex: null,
      editingOptionValue: '',
    };
  });

  return merged;
};

const toCustomColumnsPayload = (fields: CustomFieldConfig[]): CustomColumnPreference[] =>
  fields.map((field) => {
    const normalized = normalizeCustomFieldForSave(field);
    return {
      id: normalized.id,
      name: normalized.name,
      inputType: normalized.inputType,
      options: normalized.options,
    };
  });

const areCustomFieldsEqual = (left: CustomFieldConfig, right: CustomFieldConfig): boolean => {
  const leftNormalized = normalizeCustomFieldForSave(left);
  const rightNormalized = normalizeCustomFieldForSave(right);

  if (leftNormalized.name !== rightNormalized.name) return false;
  if (leftNormalized.inputType !== rightNormalized.inputType) return false;
  if (leftNormalized.options.length !== rightNormalized.options.length) return false;

  return leftNormalized.options.every((option, index) => option === rightNormalized.options[index]);
};

const getEditingOptionValidationError = (field: CustomFieldConfig): 'duplicate' | null => {
  if (field.editingOptionIndex === null) {
    return null;
  }

  const trimmed = field.editingOptionValue.trim();
  if (!trimmed) {
    return null;
  }

  const duplicate = field.options.some((option, optionIndex) => {
    if (optionIndex === field.editingOptionIndex) {
      return false;
    }
    return option.trim().toLowerCase() === trimmed.toLowerCase();
  });

  return duplicate ? 'duplicate' : null;
};

const ColumnVisibilityMenu: React.FC<ColumnVisibilityMenuProps> = ({
  value,
  onChange,
  showCustomFieldsSection = false,
  canCustomizeCustomColumns = true,
  initialCustomColumns,
  onSaveCustomColumns,
  hiddenKeys = [],
  disabledKeys = [],
  preserveOnHideAll = [],
  className,
  roleAssignmentColumns = [],
  roleColumnVisibility = {},
  onRoleColumnVisibilityChange,
  isAccountAdmin = false,
  teamSettingsRolesPath = '/team_settings?tab=roles',
  account,
  handleRefetchRoles,
  teamMembers = [],
  assignees = [],
  handleUpdateTeamMember,
  handleUpdateAssignee,
  unsetRoleColumns = [],
  handleOpenInvitePanel,
  canAccessTeamSettings,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [saveLoadingFieldId, setSaveLoadingFieldId] = useState<string | null>(null);
  const [savedCustomFields, setSavedCustomFields] = useState<CustomFieldConfig[]>(() =>
    normalizeCustomColumnsForState(initialCustomColumns)
  );
  const [customFields, setCustomFields] = useState<CustomFieldConfig[]>(() =>
    normalizeCustomColumnsForState(initialCustomColumns)
  );
  const [newRole, setNewRole] = useState<boolean>(false);
  const [newRoleValue, setNewRoleValue] = useState<string>('');
  const [formErrors, setFormErrors] = useState<FormErrors>(initialFormErrors);
  const [roleOpened, setRoleOpened] = useState<{
    label: any;
    active: boolean;
  }>({
    label: null,
    active: false,
  });
  const manageEntries = useMemo(
    () => buildRoleAssignmentManageEntries(teamMembers, assignees),
    [teamMembers, assignees]
  );

  useEffect(() => {
    if (open) return;
    const normalized = normalizeCustomColumnsForState(initialCustomColumns);
    setSavedCustomFields(normalized);
    setCustomFields(cloneCustomFields(normalized));
  }, [initialCustomColumns, open]);

  const handleFormErrors = (key: string, active: boolean = false, message: string = '') => {
    setFormErrors((prev) => ({ ...prev, [key]: { active, message } }));
  };

  const handleResetFormErrors = () => {
    setFormErrors(initialFormErrors);
  };

  const handleLoading = (val: boolean) => {
    setLoading(val);
  };

  const handleOpenRole = (item: any) => {
    setRoleOpened((prev) => {
      return {
        ...prev,
        label: prev.label?.id === item?.id ? null : item,
        active: prev.label && prev.label?.id !== item?.id ? true : !prev.active,
      };
    });
  };

  const handleNewRole = (val: boolean) => {
    if (!val) {
      handleResetFormErrors();
      setNewRoleValue('');
    }
    setNewRole(val);
  };

  // Focus new role input field
  useEffect(() => {
    let el = document.getElementById('newRoleInput');
    if (el) el.focus();
  }, [newRole]);

  const handleNewRoleValue = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setNewRoleValue(value);
    setFormErrors((prev) => ({ ...prev, name: { active: false, message: '' } }));
  };

  const handleCloseColumnsFilter = (val: boolean) => {
    setOpen(val);
    if (!val) {
      setCustomFields(cloneCustomFields(savedCustomFields));
      handleResetFormErrors();
      setNewRoleValue('');
      handleNewRole(false);
      setRoleOpened({
        label: null,
        active: false,
      });
    }
  };

  const resolved = resolveColumnVisibility(value);
  const visibleColumns = PROJECT_OVERVIEW_COLUMNS.filter(
    (column) => !hiddenKeys.includes(column.key)
  );

  const getCustomColumnNameByVisibilityKey = (key: string): string | null => {
    if (key === 'customColumn1') return savedCustomFields[0]?.name || 'Custom Field 1';
    if (key === 'customColumn2') return savedCustomFields[1]?.name || 'Custom Field 2';
    if (key === 'customColumn3') return savedCustomFields[2]?.name || 'Custom Field 3';
    return null;
  };

  const handleToggle = (key: ProjectOverviewColumnKey) => {
    if (disabledKeys.includes(key)) return;
    const next = {
      ...resolved,
      [key]: !resolved[key],
    };
    const hasAnyVisible = Object.entries(next).some(([k, v]) => {
      return v && !hiddenKeys.includes(k as ProjectOverviewColumnKey);
    });
    if (!hasAnyVisible) {
      next.client = true;
    }
    onChange(next);
  };

  const handleShowAll = () => {
    const next = { ...resolved };
    visibleColumns.forEach((column) => {
      if (disabledKeys.includes(column.key)) return;
      next[column.key] = true;
    });
    onChange(next);
    setAllRoleColumnsVisibility(true);
  };

  const handleHideAll = () => {
    const next = { ...resolved };
    visibleColumns.forEach((column) => {
      if (disabledKeys.includes(column.key)) return;
      next[column.key] = preserveOnHideAll.includes(column.key);
    });
    if (!next.client) {
      next.client = true;
    }
    onChange(next);
    setAllRoleColumnsVisibility(false);
  };

  const updateCustomField = (
    fieldId: string,
    updater: (field: CustomFieldConfig) => CustomFieldConfig
  ) => {
    setCustomFields((prev) => prev.map((field) => (field.id === fieldId ? updater(field) : field)));
  };

  const handleChangeCustomFieldName = (fieldId: string, nextName: string) => {
    updateCustomField(fieldId, (field) => ({ ...field, name: nextName }));
  };

  const handleChangeCustomFieldType = (fieldId: string, nextType: CustomFieldInputType) => {
    updateCustomField(fieldId, (field) => ({ ...field, inputType: nextType }));
  };

  const handleChangePendingOption = (fieldId: string, pendingOption: string) => {
    updateCustomField(fieldId, (field) => ({ ...field, pendingOption }));
  };

  const handleAddDropdownOption = (fieldId: string) => {
    updateCustomField(fieldId, (field) => {
      const trimmedOption = field.pendingOption.trim();
      if (!trimmedOption) {
        return field;
      }

      const validationError = getPendingOptionValidationError(field);
      if (validationError) {
        return field;
      }

      return {
        ...field,
        options: [...normalizeDropdownOptions(field.options, MAX_DROPDOWN_OPTIONS), trimmedOption],
        pendingOption: '',
      };
    });
  };

  const handleRemoveDropdownOption = (fieldId: string, optionIndex: number) => {
    updateCustomField(fieldId, (field) => ({
      ...field,
      options: field.options.filter((_, idx) => idx !== optionIndex),
      editingOptionIndex:
        field.editingOptionIndex !== null && field.editingOptionIndex === optionIndex
          ? null
          : field.editingOptionIndex,
      editingOptionValue:
        field.editingOptionIndex !== null && field.editingOptionIndex === optionIndex
          ? ''
          : field.editingOptionValue,
    }));
  };

  const handleStartEditDropdownOption = (fieldId: string, optionIndex: number) => {
    updateCustomField(fieldId, (field) => ({
      ...field,
      editingOptionIndex: optionIndex,
      editingOptionValue: field.options[optionIndex] || '',
    }));
  };

  const handleChangeEditingDropdownOption = (fieldId: string, nextValue: string) => {
    updateCustomField(fieldId, (field) => ({
      ...field,
      editingOptionValue: nextValue,
    }));
  };

  const handleCancelEditingDropdownOption = (fieldId: string) => {
    updateCustomField(fieldId, (field) => ({
      ...field,
      editingOptionIndex: null,
      editingOptionValue: '',
    }));
  };

  const handleCommitEditingDropdownOption = (fieldId: string) => {
    updateCustomField(fieldId, (field) => {
      if (field.editingOptionIndex === null) {
        return field;
      }

      const trimmed = field.editingOptionValue.trim();
      if (!trimmed) {
        return {
          ...field,
          editingOptionIndex: null,
          editingOptionValue: '',
        };
      }
      const duplicateError = getEditingOptionValidationError(field);
      if (duplicateError) {
        return {
          ...field,
          editingOptionIndex: null,
          editingOptionValue: '',
        };
      }

      const nextOptions = field.options.map((option, optionIndex) =>
        optionIndex === field.editingOptionIndex ? trimmed : option
      );

      return {
        ...field,
        options: normalizeDropdownOptions(nextOptions, MAX_DROPDOWN_OPTIONS),
        editingOptionIndex: null,
        editingOptionValue: '',
      };
    });
  };

  const handleSaveCustomField = async (fieldId: string) => {
    const draftField = customFields.find((item) => item.id === fieldId);
    if (!draftField) return;

    const nextSaved = savedCustomFields.map((field) =>
      field.id === fieldId ? normalizeCustomFieldForSave(draftField) : field
    );

    try {
      setSaveLoadingFieldId(fieldId);
      if (typeof onSaveCustomColumns === 'function') {
        await onSaveCustomColumns(toCustomColumnsPayload(nextSaved));
      }
      setSavedCustomFields(nextSaved);
      setCustomFields(cloneCustomFields(nextSaved));
    } catch (error) {
      console.error('Failed to save custom columns preference:', error);
    } finally {
      setSaveLoadingFieldId(null);
    }
  };

  const handleCancelCustomFieldChanges = (fieldId: string) => {
    const savedField = savedCustomFields.find((field) => field.id === fieldId);
    if (!savedField) return;
    updateCustomField(fieldId, () => ({
      ...savedField,
      options: [...savedField.options],
      pendingOption: '',
    }));
  };

  const handleRoleToggle = (roleId: number) => {
    if (typeof onRoleColumnVisibilityChange !== 'function') {
      return;
    }
    const next = {
      ...roleColumnVisibility,
      [roleId]: roleColumnVisibility[roleId] === false,
    };
    onRoleColumnVisibilityChange(next);
  };

  const setAllRoleColumnsVisibility = (visible: boolean) => {
    if (typeof onRoleColumnVisibilityChange !== 'function' || roleAssignmentColumns.length === 0) {
      return;
    }
    const next = { ...roleColumnVisibility };
    roleAssignmentColumns.forEach((role) => {
      next[role.id] = visible;
    });
    onRoleColumnVisibilityChange(next);
  };

  const handleCreateNewRole = async () => {
    if (!account) return;
    let trimmedNewRoleName = newRoleValue?.trim();
    if (!trimmedNewRoleName) {
      handleFormErrors('name', true, 'Required field.');
      return;
    }

    handleResetFormErrors();
    handleLoading(true);

    const payload = {
      accountId: account.parentAccountId || account.id,
      name: trimmedNewRoleName,
      items: false,
      projects: true,
    };

    try {
      const res = await addNewRole(payload);
      if (res) {
        setNewRole(false);
        setNewRoleValue('');

        if (handleRefetchRoles) {
          handleRefetchRoles();
        }
      }
    } catch (error) {
      if ((error as any)?.response?.status === 409) {
        showErrorToast('Role already exists. Please use a different name.');
        return;
      }
      if ((error as any)?.response?.status === 403) {
        showErrorToast('You do not have permission to add roles.');
        return;
      }
      showErrorToast('Failed to add role. Please try again later.');
    } finally {
      handleLoading(false);
    }
  };

  // const handleUpdateTeamMember = async (val: any, member: any, role: any) => {
  //   if (!member) return;
  //   if (!role) return;

  //   let memberRoles: any[] = [];
  //   if (val) {
  //     memberRoles = [...member?.roles, role?.id];
  //   } else if (member?.roles?.length > 0) {
  //     memberRoles = member?.roles?.filter((f: any) => f !== role?.id);
  //   }

  //   let payload = {
  //     accountId: member?.accountId,
  //     roles: memberRoles,
  //   };

  //   if (handleUpdateTeamMembers) {
  //     let newTeamMembers = teamMembers?.map((i) => {
  //       if (i?.userId === member?.userId) {
  //         return {
  //           ...i,
  //           roles: memberRoles,
  //         };
  //       }
  //       return i;
  //     });
  //     handleUpdateTeamMembers(newTeamMembers);
  //   }

  //   const res = await updateAccount(payload);
  //   if (!res) {
  //     console.error('Error while updating Account');
  //   }
  // };

  const handleAddRoleToList = async (item: any) => {
    if (!item) return;

    let payload = {
      id: item?.id,
      projects: true,
      accountId: account?.parentAccountId || account?.id,
    };

    const res = await editRole(payload);
    if (res) {
      if (handleRefetchRoles) {
        handleRefetchRoles();
      }
    }
  };

  const hasRoleColumns = roleAssignmentColumns.length > 0;
  const hasMultiUserSeats = !!canAccessTeamSettings;

  const showAssigneeSection = hasMultiUserSeats && (hasRoleColumns || isAccountAdmin);

  return (
    <Popover open={open} onOpenChange={(val) => handleCloseColumnsFilter(val)}>
      <PopoverTrigger data-qa={`projects_columns_dropdown_trigger`} asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 border border-input bg-background hover:border-input-hover h-9 rounded-md px-3',
            className
          )}
        >
          <Settings className="w-[16px] h-[16px]" />
          Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionPadding={8}
        className="w-[min(320px,calc(100vw-1rem))] p-4"
      >
        <div className="flex flex-col gap-3 md:max-h-[calc(100dvh-340px)] max-h-[calc(100dvh-370px)] overflow-y-auto pr-[5px]">
          <div className="text-sm font-semibold">Column Visibility</div>
          <div className="flex flex-col gap-[8px] pr-1">
            {visibleColumns.map((column) => {
              const isDisabled = disabledKeys.includes(column.key);
              return (
                <div
                  key={column.key}
                  className={cn(
                    'flex items-start justify-between gap-2 text-sm',
                    isDisabled && 'opacity-60'
                  )}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-sm line-clamp-2 min-w-0 flex-1 cursor-default">
                        {getCustomColumnNameByVisibilityKey(column.key) || column.label}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {getCustomColumnNameByVisibilityKey(column.key) || column.label}
                    </TooltipContent>
                  </Tooltip>
                  <Switch
                    data-qa={`projects_columns_${column?.key}`}
                    checked={resolved[column.key]}
                    disabled={isDisabled}
                    onClick={() => handleToggle(column.key)}
                  />
                </div>
              );
            })}
          </div>
          {showCustomFieldsSection && canCustomizeCustomColumns && (
            <>
              <div className="w-full bg-input h-[1px] shrink-0" />

              <div className="flex flex-col gap-4 pr-1">
                <div className="text-sm font-semibold">Customize Custom Fields</div>

                {customFields.map((field, index) => {
                  const isDropdown = field.inputType === 'dropdown';
                  const inputTypeId = `custom-field-input-type-${field.id}`;
                  const hasPendingOption = field.pendingOption.trim().length > 0;
                  const pendingOptionError = getPendingOptionValidationError(field);
                  const editingOptionError = getEditingOptionValidationError(field);
                  const savedField =
                    savedCustomFields.find((item) => item.id === field.id) || field;
                  const hasChanges = !areCustomFieldsEqual(field, savedField);

                  return (
                    <div key={field.id} className="flex flex-col gap-2.5">
                      <div className="text-sm font-semibold">{`Custom Field ${index + 1} Name`}</div>

                      <Input
                        value={field.name}
                        onChange={(e) => handleChangeCustomFieldName(field.id, e.target.value)}
                        placeholder={`Custom Field ${index + 1}`}
                        className="h-9 text-sm"
                      />

                      <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold text-[#6E7D90] whitespace-nowrap">
                          Input Type:
                        </label>

                        <div id={inputTypeId} className="w-[140px]">
                          <Select
                            dataQa={`${inputTypeId}_select`}
                            value={
                              CUSTOM_FIELD_INPUT_TYPE_OPTIONS.find(
                                (option) => option.value === field.inputType
                              )?.label || 'Plain text'
                            }
                            items={CUSTOM_FIELD_INPUT_TYPE_OPTIONS}
                            className="h-8 min-h-8 text-sm font-medium"
                            onSelect={(selectedOption) => {
                              const nextType =
                                typeof selectedOption === 'string'
                                  ? selectedOption.toLowerCase()
                                  : selectedOption?.value;
                              if (nextType === 'text' || nextType === 'dropdown') {
                                handleChangeCustomFieldType(field.id, nextType);
                              }
                            }}
                          />
                        </div>
                      </div>

                      {isDropdown && (
                        <div className="flex flex-col gap-2">
                          <div className="text-xs font-semibold text-[#6E7D90]">
                            Dropdown Values
                          </div>

                          <div className="flex items-center gap-2">
                            <Input
                              value={field.pendingOption}
                              onChange={(e) => handleChangePendingOption(field.id, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddDropdownOption(field.id);
                                }
                              }}
                              placeholder="New value"
                              className="h-8 text-sm"
                            />
                            <button
                              type="button"
                              disabled={!hasPendingOption || pendingOptionError !== null}
                              onClick={() => handleAddDropdownOption(field.id)}
                              className="h-8 min-h-8 px-0 text-sm text-[#6E7D90] disabled:opacity-50 disabled:cursor-not-allowed hover:text-foreground transition-colors"
                            >
                              Add
                            </button>
                          </div>

                          {hasPendingOption && pendingOptionError === 'duplicate' && (
                            <div className="text-xs text-destructive">Option already exists.</div>
                          )}
                          {hasPendingOption && pendingOptionError === 'limit' && (
                            <div className="text-xs text-destructive">
                              Maximum of {MAX_DROPDOWN_OPTIONS} options allowed.
                            </div>
                          )}

                          {normalizeDropdownOptions(field.options, MAX_DROPDOWN_OPTIONS).map(
                            (option, optionIndex) => (
                              <div
                                key={`${field.id}-option-${optionIndex}`}
                                className="flex items-center gap-2"
                              >
                                {field.editingOptionIndex === optionIndex ? (
                                  <>
                                    <Input
                                      value={field.editingOptionValue}
                                      autoFocus
                                      onChange={(event) =>
                                        handleChangeEditingDropdownOption(
                                          field.id,
                                          event.target.value
                                        )
                                      }
                                      onBlur={() => handleCommitEditingDropdownOption(field.id)}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                          event.preventDefault();
                                          event.currentTarget.blur();
                                        }
                                        if (event.key === 'Escape') {
                                          event.preventDefault();
                                          handleCancelEditingDropdownOption(field.id);
                                        }
                                      }}
                                      className="h-8 text-sm"
                                    />
                                  </>
                                ) : (
                                  <>
                                    <div className="flex-1 flex items-center gap-1.5">
                                      <div className="text-sm">{option}</div>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleStartEditDropdownOption(field.id, optionIndex)
                                        }
                                        aria-label={`Edit option ${option}`}
                                        className="text-[#3B97B1] hover:text-[#2f7d93] transition-colors p-0.5"
                                      >
                                        <LucideSquarePen className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="x_btn"
                                      size="x_btn"
                                      onClick={() =>
                                        handleRemoveDropdownOption(field.id, optionIndex)
                                      }
                                      aria-label={`Remove option ${option}`}
                                    >
                                      <LucideX className="h-3.5 w-3.5" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            )
                          )}
                          {field.editingOptionIndex !== null &&
                            editingOptionError === 'duplicate' && (
                              <div className="text-xs text-destructive">Option already exists.</div>
                            )}
                        </div>
                      )}

                      <div className="flex items-center justify-end gap-2">
                        {hasChanges && (
                          <button
                            type="button"
                            onClick={() => handleCancelCustomFieldChanges(field.id)}
                            className="h-8 px-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                          >
                            Cancel
                          </button>
                        )}

                        <Button
                          type="button"
                          size="sm"
                          disabled={!hasChanges || saveLoadingFieldId === field.id}
                          onClick={() => void handleSaveCustomField(field.id)}
                          className="h-8 min-h-8 min-w-[56px] px-3 text-sm"
                        >
                          Save
                        </Button>
                      </div>

                      {index < customFields.length - 1 && (
                        <div className="w-full bg-input h-[1px] shrink-0 mt-1" />
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
          {showAssigneeSection && (
            <div className="border-t border-input pt-3 flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-semibold">Assignee Columns</div>
                  {showAssigneeColumnsNewBadge ? <span className="new_pill">New</span> : null}
                </div>
                {isAccountAdmin && (
                  <Link
                    data-qa={`manage_teams_and_roles_link`}
                    to={teamSettingsRolesPath}
                    className="text-xs font-medium text-[#3B97B1] hover:underline"
                  >
                    Manage Team &amp; Roles
                  </Link>
                )}
              </div>
              {hasRoleColumns ? (
                <div className="flex flex-col gap-2 overflow-y-auto pr-1">
                  {roleAssignmentColumns.map((role) => {
                    const activeRoleDropdown = role.id === roleOpened.label?.id;
                    const roleUsageCount = countRoleAssignmentManageEntriesForRole(
                      manageEntries,
                      role.id
                    );

                    return (
                      <Fragment key={role.id}>
                        <div className="flex items-center justify-between text-sm">
                          <div className="text-sm flex items-center gap-2">
                            <span className="font-medium text-sm">{role.name}</span>
                            {isAccountAdmin && (
                              <button
                                onClick={() => handleOpenRole(role)}
                                data-qa={`assignment_column_${role?.name}_team_members`}
                                type="button"
                                disabled={!isAccountAdmin}
                                className="text-xs text-muted-foreground hover:text-primary flex items-center gap-0.5"
                              >
                                <span>{`(${roleUsageCount || 0})`}</span>
                                <span className="p-0.5">
                                  <ChevronRight
                                    data-open={activeRoleDropdown}
                                    className={`lucide lucide-chevron-right h-3 w-3 transform data-[open=true]:rotate-90 transition`}
                                  />
                                </span>
                              </button>
                            )}
                          </div>

                          <Switch
                            checked={roleColumnVisibility[role.id] !== false}
                            onClick={() => handleRoleToggle(role.id)}
                          />
                        </div>

                        {role?.id === roleOpened.label?.id && (
                          <div>
                            <div className="pl-4 pt-2 pb-2 space-y-1 border-l-2 border-muted ml-2 mt-1">
                              {manageEntries.map((entry) => (
                                <div key={entry.key} className="flex items-center gap-2">
                                  <Checkbox
                                    checked={entry.roleIds.includes(role?.id)}
                                    data-qa={`assignment_column_${role?.name}_checkbox_trigger`}
                                    onCheckedChange={(val) => {
                                      if (entry.type === 'team_member') {
                                        handleUpdateTeamMember?.(val, entry.teamMember, role);
                                        return;
                                      }
                                      handleUpdateAssignee?.(val, entry.assignee, role);
                                    }}
                                  />
                                  <span className="text-sm">{entry.name}</span>
                                  {entry.label ? (
                                    <span className="text-xs text-muted-foreground">
                                      @{entry.label}
                                    </span>
                                  ) : null}
                                </div>
                              ))}

                              <button
                                data-qa={`add_team_member_to_role_${role?.name}`}
                                onClick={() => {
                                  if (handleOpenInvitePanel) {
                                    handleOpenInvitePanel('team_member', role);
                                    handleCloseColumnsFilter(false);
                                  }
                                }}
                                type="button"
                                className="text-xs text-primary hover:underline mt-2 flex items-center gap-1"
                              >
                                <LucidePlus className="lucide lucide-plus h-3 w-3" />
                                Add Team Member
                              </button>
                            </div>
                          </div>
                        )}
                      </Fragment>
                    );
                  })}

                  {isAccountAdmin &&
                    (newRole ? (
                      <div className="flex items-center gap-2 p-1.5">
                        <Input
                          id={'newRoleInput'}
                          placeholder="Role name"
                          data-qa={`columns_new_role_input`}
                          name="newRoleValue"
                          value={newRoleValue}
                          autoFocus
                          disabled={loading}
                          onChange={handleNewRoleValue}
                          onKeyDown={(e) => e.key === 'Enter' && handleCreateNewRole()}
                          data-error={formErrors?.name?.active}
                          className={`min-h-[32px] data-[error=true]:border-red-500 data-[error=true]:shadow-[0_0_3px_0_rgba(255,0,0,0.5)] transition`}
                        />

                        <button
                          data-qa={`columns_new_role_create_trigger`}
                          onClick={() => handleCreateNewRole()}
                          disabled={loading}
                          type="button"
                          className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                        >
                          Add
                        </button>

                        <button
                          data-qa={`columns_new_role_cancel_trigger`}
                          onClick={() => handleNewRole(false)}
                          disabled={loading}
                          type="button"
                          className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                        >
                          <LucideX className="lucide lucide-x h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        data-qa={`columns_new_role_add_trigger`}
                        onClick={() => handleNewRole(true)}
                        type="button"
                        className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent h-9 rounded-md px-3 w-full justify-start text-primary hover:text-primary"
                      >
                        <LucidePlus className="lucide lucide-plus h-4 w-4 mr-2" />
                        Add Role
                      </button>
                    ))}
                </div>
              ) : (
                isAccountAdmin && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <div className="bg-muted/50 rounded-md p-3">
                        <span className="text-xs text-muted-foreground">
                          No roles assigned to this list yet.
                        </span>
                      </div>
                      {newRole ? (
                        <div className="flex items-center gap-2 p-1.5">
                          <Input
                            id={'newRoleInput'}
                            placeholder="Role name"
                            data-qa={`columns_new_role_input`}
                            name="newRoleValue"
                            value={newRoleValue}
                            autoFocus
                            disabled={loading}
                            onChange={handleNewRoleValue}
                            onKeyDown={(e) => e.key === 'Enter' && handleCreateNewRole()}
                            data-error={formErrors?.name?.active}
                            className={`min-h-[32px] data-[error=true]:border-red-500 data-[error=true]:shadow-[0_0_3px_0_rgba(255,0,0,0.5)] transition`}
                          />

                          <button
                            data-qa={`columns_new_role_create_trigger`}
                            onClick={() => handleCreateNewRole()}
                            disabled={loading}
                            type="button"
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                          >
                            Add
                          </button>

                          <button
                            data-qa={`columns_new_role_cancel_trigger`}
                            onClick={() => handleNewRole(false)}
                            disabled={loading}
                            type="button"
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                          >
                            <LucideX className="lucide lucide-x h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          data-qa={`columns_new_role_add_trigger`}
                          onClick={() => handleNewRole(true)}
                          type="button"
                          className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent h-9 rounded-md px-3 w-full justify-start text-primary hover:text-primary"
                        >
                          <LucidePlus className="lucide lucide-plus h-4 w-4 mr-2" />
                          Add Role
                        </button>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          )}

          {hasMultiUserSeats && isAccountAdmin && unsetRoleColumns?.length > 0 && (
            <>
              <div className="w-full bg-input h-[1px] shrink-0" />

              <div className="space-y-2">
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-muted-foreground">Available Roles</span>
                  <span className="text-xs text-muted-foreground">Not assigned to this list</span>
                </div>

                {unsetRoleColumns?.map((i: any, idx: number) => {
                  const roleUsageCount = countRoleAssignmentManageEntriesForRole(
                    manageEntries,
                    i?.id
                  );

                  return (
                    <div
                      key={idx}
                      className="flex items-center justify-between text-muted-foreground"
                    >
                      <div className="text-sm flex items-center gap-2">
                        <span>{i?.name || ''}</span>
                        <span className="text-xs">{`(${roleUsageCount})`}</span>
                      </div>

                      <button
                        onClick={() => handleAddRoleToList(i)}
                        data-qa={`available_roles_${i?.name}_add_to_list_trigger`}
                        type="button"
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground rounded-md px-3 h-6 text-xs text-primary"
                      >
                        <LucidePlus className="lucide lucide-plus h-3 w-3 mr-1" />
                        Add to List
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div>
          <div className="border-t border-input bg-background pt-3 mt-3 flex items-center gap-2">
            <Button
              data-qa={`projects_columns_show_all_trigger`}
              variant="outline"
              size="sm"
              className="min-h-[36px] inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:border-input-hover h-9 rounded-md px-3 flex-1"
              onClick={handleShowAll}
            >
              Show all
            </Button>
            <Button
              data-qa={`projects_columns_hide_all_trigger`}
              variant="outline"
              size="sm"
              className="min-h-[36px] inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:border-input-hover h-9 rounded-md px-3 flex-1"
              onClick={handleHideAll}
            >
              Hide all
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ColumnVisibilityMenu;
